import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, CalendarClock, CalendarOff, ExternalLink, MapPin, MoreHorizontal,
  Pencil, Plus, Search, ShieldCheck, Trash2,
} from "lucide-react";

import { EventFormDialog } from "@/components/EventFormDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useEvents } from "@/hooks/useEvents";
import { useDeleteEvent } from "@/hooks/useEventMutations";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { formatDateTime } from "@/lib/format";
import type { CurrentUser, EventRow } from "@/lib/types";
import { cn } from "@/lib/utils";

type StatusFilter = "" | "upcoming" | "past" | "draft";

const STATUS: { value: StatusFilter; label: string }[] = [
  { value: "",         label: "All events" },
  { value: "upcoming", label: "Upcoming" },
  { value: "past",     label: "Past" },
  { value: "draft",    label: "Drafts" },
];

/**
 * /events — card grid (visual, since events have a strong "cover
 * image" affordance). Filter bar at the top, masonry-ish layout below.
 */
export default function Events() {
  const { data: user } = useCurrentUser();
  const [search,      setSearch]      = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [status,      setStatus]      = useState<StatusFilter>("");
  const [page,        setPage]        = useState(1);
  const [createOpen,  setCreateOpen]  = useState(false);
  const [editing,     setEditing]     = useState<EventRow | null>(null);

  const { data, isLoading } = useEvents({
    search: search || undefined, status: status || undefined, page,
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Events</h1>
          <p className="text-sm text-muted-foreground">
            Convenings, workshops, and field engagements hosted by the Ministry.
          </p>
        </div>
        {user?.is_director_level && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />New event
          </Button>
        )}
      </header>

      <EventFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EventFormDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        event={editing}
      />

      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <form
            className="relative flex-1"
            onSubmit={(e) => { e.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search title, summary, location…"
              className="h-10 pl-9"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </form>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value as StatusFilter); setPage(1); }}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm font-medium"
          >
            {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64 w-full" />
          ))}
        </div>
      ) : data && data.results.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.results.map((e) => (
            <EventCard
              key={e.id}
              event={e}
              user={user ?? null}
              onEdit={() => setEditing(e)}
            />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <CalendarOff className="h-5 w-5 text-muted-foreground" />
            </div>
            <h3 className="text-sm font-semibold">No events match</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Try clearing the filter, or create the first one.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function EventCard({
  event, user, onEdit,
}: {
  event: EventRow;
  user: CurrentUser | null;
  onEdit: () => void;
}) {
  const canManage = !!user && user.is_director_level;
  return (
    <Card className="group flex flex-col overflow-hidden border-border/60 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-soft-lg">
      {/* Hero image (or solid block if none). */}
      <div className="relative h-40 overflow-hidden bg-gradient-to-br from-emerald-100 to-emerald-50">
        {event.image_url ? (
          <img
            src={event.image_url} alt=""
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <CalendarClock className="h-10 w-10 text-emerald-700 dark:text-emerald-300/50" />
          </div>
        )}
        {/* Date chip — top-left. */}
        <div className="absolute left-3 top-3 rounded-lg bg-card/95 px-2.5 py-1.5 text-center shadow-soft backdrop-blur">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            {new Date(event.start_at).toLocaleDateString(undefined, { month: "short" })}
          </div>
          <div className="font-mono text-lg font-bold leading-none tabular-nums">
            {new Date(event.start_at).getDate()}
          </div>
        </div>
        {/* Status chip + actions menu — top-right. */}
        <div className="absolute right-3 top-3 flex items-center gap-1">
          <StatusBadge event={event} />
          {canManage && <EventCardActions event={event} onEdit={onEdit} />}
        </div>
      </div>

      <CardContent className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <Badge variant="outline" className="bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30">
            {event.event_type_display}
          </Badge>
        </div>
        <h3 className="line-clamp-2 text-base font-semibold leading-snug">
          <Link to={`/events/${event.id}`} className="hover:text-primary hover:underline">
            {event.title}
          </Link>
        </h3>

        <div className="space-y-1.5 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5" />
            {formatDateTime(event.start_at)}
          </div>
          {event.location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
        </div>

        {event.summary && (
          <p className="line-clamp-2 text-xs text-muted-foreground">
            {event.summary}
          </p>
        )}

        {event.registration_url && (
          <div className="mt-auto pt-2">
            <Button asChild variant="outline" size="sm" className="w-full">
              <a href={event.registration_url} target="_blank" rel="noreferrer">
                Register
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ event }: { event: EventRow }) {
  if (event.needs_ceo_approval) {
    return (
      <Badge variant="outline" className={cn("border bg-violet-50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30")}>
        <ShieldCheck className="mr-1 h-3 w-3" />Pending CEO
      </Badge>
    );
  }
  if (!event.is_published) {
    return <Badge variant="outline" className="border bg-muted/40 text-muted-foreground border-border">Draft</Badge>;
  }
  if (event.is_past) {
    return <Badge variant="outline" className="border bg-muted text-foreground/80 border-border">Past</Badge>;
  }
  return (
    <Badge variant="outline" className="border bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30">
      <CalendarCheck className="mr-1 h-3 w-3" />Upcoming
    </Badge>
  );
}

/** Per-card kebab menu for Director+ — Edit / Delete. Floats over
 *  the cover image alongside the status badge. */
function EventCardActions({
  event, onEdit,
}: { event: EventRow; onEdit: () => void }) {
  const del = useDeleteEvent();
  const confirm = useConfirm();

  async function onDelete() {
    const ok = await confirm({
      title: `Delete "${event.title}"?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(event.id);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost" size="icon"
          className="h-7 w-7 bg-card/95 hover:bg-card shadow-soft backdrop-blur"
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="mr-2 h-4 w-4" />Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          disabled={del.isPending}
          onSelect={(e) => { e.preventDefault(); onDelete(); }}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {del.isPending ? "Deleting…" : "Delete"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
